const requestHeaders = () => ({
    'Content-Type': 'application/json; charset=UTF-8',
    'Authorization': sessionStorage.getItem('agronetToken'),
    'Local': localStorage.getItem('lang') || 'en'
});

const apiUrl = path =>
    /^https?:\/\//i.test(path)
        ? path
        : `${process.env.REACT_APP_API_URL}${path}`;

const getFilename = (response, fallback) => {
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition && disposition.match(/filename="?([^"]+)"?/i);
    return match ? match[1] : fallback || 'sensor-group-report.pdf';
};

const parseJsonResponse = async response => {
    if (response.ok) {
        return response.json();
    }

    let message = `Error: ${response.status} ${response.statusText}`;
    try {
        const error = await response.json();
        message = error.detail || error.message || error.error || message;
    } catch (ignored) {
        // The default HTTP error is used when the response has no JSON body.
    }
    throw new Error(message);
};

const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
        reject(new DOMException('The operation was aborted.', 'AbortError'));
        return;
    }

    const timeout = setTimeout(resolve, milliseconds);
    if (signal) {
        signal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new DOMException('The operation was aborted.', 'AbortError'));
        }, {once: true});
    }
});

const getSessionStatus = (sessionId, signal) =>
    fetch(apiUrl(`/reports/sensor-groups/sessions/${sessionId}`), {
        headers: requestHeaders(),
        signal
    }).then(parseJsonResponse);

const waitForCompletedSession = async (session, signal) => {
    let currentSession = session;
    while (currentSession.status === 'PENDING' || currentSession.status === 'PROCESSING') {
        await wait(6000, signal);
        currentSession = await getSessionStatus(currentSession.sessionId, signal);
    }

    if (currentSession.status === 'COMPLETED') {
        return currentSession;
    }

    throw new Error(
        currentSession.errorMessage ||
        (currentSession.status === 'EXPIRED'
            ? 'The sensor group report has expired.'
            : 'Sensor group report generation failed.')
    );
};

const downloadCompletedSession = async (session, signal) => {
    const downloadUrl = session.downloadUrl ||
        `/reports/sensor-groups/sessions/${session.sessionId}/file`;
    const response = await fetch(apiUrl(downloadUrl), {
        headers: requestHeaders(),
        signal
    });
    if (!response.ok) {
        await parseJsonResponse(response);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getFilename(response, session.fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const downloadSensorGroupReportPdf = (request, signal) => () =>
    fetch(apiUrl('/reports/sensor-groups/sessions'), {
        method: 'POST',
        headers: requestHeaders(),
        body: JSON.stringify(request),
        signal
    })
        .then(parseJsonResponse)
        .then(session => waitForCompletedSession(session, signal))
        .then(session => downloadCompletedSession(session, signal));
